import { loadStripe } from '@stripe/stripe-js'
import { finalizeSubscriptionAfterPayment, payNow } from 'api/subscription'
import { useAuthContext } from 'components/context/AuthProvider'
import NotificationSys from 'components/NotificationSystem'
import { STRIPE_KEY } from 'const'
import { useState } from 'react'
import { UserBillingProfile, UserSubscription } from 'types'
import { useInvalidateBillingProfile } from './useSubscription'

export function usePayNow({
  billingProfile,
  subscription,
  onNeedCard,
}: {
  billingProfile: UserBillingProfile | null
  subscription: UserSubscription | null
  onNeedCard: () => void
}) {
  const { refreshUser } = useAuthContext()
  const invalidateBillingProfile = useInvalidateBillingProfile()
  const [loading, setLoading] = useState(false)

  const handlePayNow = async () => {
    if (!billingProfile) {
      onNeedCard()
      return
    }

    try {
      setLoading(true)
      const response = await payNow(billingProfile.id, subscription?.id)
      const { status, paymentIntentClientSecret, alreadyPaid } = response.data

      if (status === 'paid' || alreadyPaid) {
        await refreshUser()
        invalidateBillingProfile()
        NotificationSys.showSuccess('Payment processed successfully.')
        return
      }

      if (status === 'requires_action' && paymentIntentClientSecret) {
        const stripe = await loadStripe(STRIPE_KEY)
        if (!stripe) return

        const { error } = await stripe.confirmCardPayment(paymentIntentClientSecret)
        if (error) {
          NotificationSys.showWarning(error.message ?? 'Payment confirmation failed.')
          return
        }

        if (subscription?.stripeSubscriptionId) {
          // Use stripeSubscriptionId — works for both individual and family (group) sources.
          // For family, subscription.id is the group id and cannot be passed to /user-subscriptions/sync { id }.
          await finalizeSubscriptionAfterPayment({
            stripeSubscriptionId: subscription.stripeSubscriptionId,
            stripeClientSecret: null,
          })
        }
        await refreshUser()
        invalidateBillingProfile()
        NotificationSys.showSuccess('Payment confirmed successfully.')
      }
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? ''

      if (
        message.includes('No saved payment method') ||
        message.includes('Please add another card')
      ) {
        NotificationSys.showWarning(message)
        onNeedCard()
      } else {
        NotificationSys.showWarning(message || 'Payment could not be processed.')
      }
    } finally {
      setLoading(false)
    }
  }

  return { handlePayNow, payNowLoading: loading }
}
